import { useEffect, useSyncExternalStore } from "react";
import { FEATURES } from "../lib/features.ts";

export type Route = "digest" | "discover" | "map" | "glossary" | "roundup" | "trends" | "briefing" | "read" | "shared" | "story" | "not_found";

const subscribe = (cb: () => void) => {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
};

export function routeFor(pathname: string): Route {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/") return FEATURES.digest ? "digest" : "discover";
  if (p === "/browse") return "discover";
  if (p === "/digest") return FEATURES.digest ? "digest" : "not_found";
  if (p === "/glossary") return "glossary";
  if (p === "/roundup") return "roundup";
  if (p === "/trends") return "trends";
  if (p === "/briefing") return FEATURES.briefing ? "briefing" : "not_found";
  if (p === "/map") return "map";
  if (/^\/story\/[a-z0-9][a-z0-9_-]{0,127}$/.test(p)) return "story";
  if (p === "/read") return "read";
  if (p === "/shared") return "shared";
  return "not_found";
}

export function navigate(to: string) {
  if (to === window.location.pathname + window.location.search) return;
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}

export function useRoute(): Route {
  const path = useSyncExternalStore(subscribe, () => window.location.pathname);
  const route = routeFor(path);
  useEffect(() => {
    const titles: Record<Route, string> = {
      digest: "Digest · Databricks Pulse",
      discover: "Browse · Databricks Pulse",
      map: "Map · Databricks Pulse",
      glossary: "Glossary · Databricks Pulse",
      roundup: "Weekly roundup · Databricks Pulse",
      trends: "Trends · Databricks Pulse",
      story: "Update · Databricks Pulse",
      briefing: "My Briefing · Databricks Pulse",
      read: "Reading session · Databricks Pulse",
      shared: "Shared reading list · Databricks Pulse",
      not_found: "Not found · Databricks Pulse",
    };
    document.title = titles[route];
    // One description per page (search results and link previews that run scripts).
    const descriptions: Partial<Record<Route, string>> = {
      discover: "Every official Databricks release note, blog post and press release in one place, newest first, each linked to its source.",
      map: "A map of Databricks products flagged New, Preview, Beta, GA, Deprecating or Retired, with a timeline of updates for each.",
      glossary: "Plain-English definitions of every Databricks product area, with links to the timeline and the official docs.",
      roundup: "A weekly roundup of Databricks blog announcements, release highlights and takeaways, each linked to its source.",
      trends: "What Databricks Pulse readers are opening, clicking through to and saving: by period, topic and country.",
    };
    const d = descriptions[route];
    if (d) document.querySelector('meta[name="description"]')?.setAttribute("content", d);
  }, [route]);
  return route;
}

/** Same-origin link that uses client-side navigation (and still works with modifier keys). */
export function onLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  navigate(e.currentTarget.getAttribute("href") ?? "/");
}

/** Query-string state for shareable views (e.g. /map?cloud=aws&product=lakebase). */
export function useSearchParam(name: string): [string | null, (value: string | null) => void] {
  const search = useSyncExternalStore(subscribe, () => window.location.search);
  const value = new URLSearchParams(search).get(name);
  const set = (next: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (next === null || next === "") params.delete(name);
    else params.set(name, next);
    const qs = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return [value, set];
}

export function useStoryIdFromPath(): string | null {
  const path = useSyncExternalStore(subscribe, () => window.location.pathname);
  const m = path.match(/^\/story\/([a-z0-9][a-z0-9_-]{0,127})\/?$/);
  return m ? m[1] : null;
}
